import {handleSaveDraftActivity, handleCreateActivity, handleUpdateDraftActivity, handleUpdateActivity, validateActivityName, handleGetActivity} from '@/services/activities'
import {useAppDispatch, useAppSelector} from '@/store'
import {resetActivityData, setActivityFormData} from '@/store/slices/activitySlice'
import {setToastNotifications} from '@/store/slices/commonSlice'
import {ACTIVITY_TYPE, ACTIVITY_TYPES_ENUM, OrderType} from '@/types/Activity'
import {getDateFromISOString, getFormattedDateString, getUUID, formatUTCTimestamp, isPublished, handleNameValidationViaCallbackFn, isInProgress} from '@/utils/commons'
import {ROUTES} from '@/utils/constants/routesConstants'
import {useState} from 'react'
import {useSelector} from 'react-redux'
import {useNavigate} from 'react-router-dom'
import {COUNTRY_CODES_FREQUENCY, APPLICATION_ID} from '@/utils/constants/globalConstants'
import { useActivityValidation } from '@/hooks/useActivityValidation'

/**
 * Helper function to determine if an activity is being edited in published/in-progress state
 * For Point Expiration: checks for numeric ID (status may be null)
 * For other activities: checks status
 */
const checkIsEditingPublished = (activityFormData) => {
  if (activityFormData?.activityType === ACTIVITY_TYPE.POINT_EXPIRATION) {
    return !!(activityFormData?.id || activityFormData?.activityId)
  }
  return isPublished(activityFormData?.status) || isInProgress(activityFormData?.status)
}

export default function useActivityReview() {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [isCancel, setIsCancel] = useState(false)
  const activityFormData = useSelector(state => state.activity.formData)
  const [isNameValid, setIsNameValid] = useState(true)
  
  const {
    showToast,
    toastMessage,
    handleActivityDurationValidation,
    handleToastClose,
    resetValidation,
    handleViewActivity,
    handlePointExpirationPublishError,
  } = useActivityValidation()

  const getActivityEnum = activityType => {
    if (activityType === ACTIVITY_TYPE.WELCOME_ACTIVITY) {
      return ACTIVITY_TYPES_ENUM.WELCOME
    }
    if (activityType === ACTIVITY_TYPE.LOGIN) {
      return ACTIVITY_TYPES_ENUM.LOGIN
    }
    if (activityType === ACTIVITY_TYPE.PLAY_GAME) {
      return ACTIVITY_TYPES_ENUM.PLAY_GAME
    }
    if (activityType === ACTIVITY_TYPE.WATCH_VIDEO) {
      return ACTIVITY_TYPES_ENUM.WATCH_VIDEO
    }
    if (activityType === ACTIVITY_TYPE.SURVEY) {
      return ACTIVITY_TYPES_ENUM.SURVEY
    }
    if (activityType === ACTIVITY_TYPE.POINTS_TRANSFER) {
      return ACTIVITY_TYPES_ENUM.POINTS_TRANSFER
    }
    if (activityType === ACTIVITY_TYPE.POINTS_ON_ORDER_DELIVERY) {
      return ACTIVITY_TYPES_ENUM.POINTS_ON_ORDER_DELIVERY
    }
    if (activityType === ACTIVITY_TYPE.REDEEM_POINTS) {
      return ACTIVITY_TYPES_ENUM.REDEEM_POINTS
    }
    if (activityType === ACTIVITY_TYPE.GENERIC_ACTIVITY) {
      return ACTIVITY_TYPES_ENUM.GENERIC_ACTIVITY
    }
    if (activityType === ACTIVITY_TYPE.POINT_EXPIRATION) {
      return ACTIVITY_TYPES_ENUM.POINT_EXPIRATION
    }
    return null
  }

  const getDisplayActivityType = activityType => {
    switch(activityType){
      case ACTIVITY_TYPES_ENUM.WELCOME:  return ACTIVITY_TYPE.WELCOME_ACTIVITY
      case ACTIVITY_TYPES_ENUM.LOGIN: return ACTIVITY_TYPE.LOGIN
      case ACTIVITY_TYPES_ENUM.PLAY_GAME: return ACTIVITY_TYPE.PLAY_GAME
      case ACTIVITY_TYPES_ENUM.WATCH_VIDEO: return ACTIVITY_TYPE.WATCH_VIDEO
      case ACTIVITY_TYPES_ENUM.SURVEY: return ACTIVITY_TYPE.SURVEY
      case ACTIVITY_TYPES_ENUM.POINTS_TRANSFER: return ACTIVITY_TYPE.POINTS_TRANSFER
      case ACTIVITY_TYPES_ENUM.POINTS_ON_ORDER_DELIVERY: return ACTIVITY_TYPE.POINTS_ON_ORDER_DELIVERY
      case ACTIVITY_TYPES_ENUM.REDEEM_POINTS: return ACTIVITY_TYPE.REDEEM_POINTS
      case ACTIVITY_TYPES_ENUM.GENERIC_ACTIVITY: return ACTIVITY_TYPE.GENERIC_ACTIVITY
      case ACTIVITY_TYPES_ENUM.POINT_EXPIRATION: return ACTIVITY_TYPE.POINT_EXPIRATION
      default: return activityType
    }
    
  }
  const getSlug = activityType => {
    // Point Expiration should use fixed slug for mobile app compatibility
    if (activityType === ACTIVITY_TYPE.POINT_EXPIRATION) {
      return 'reward-points::rolling-points-expiry'
    }
    return `reward-points::${activityType.replace(/\s+/g, '-').toLowerCase()}-${Date.now()}`
  }

  const handleResetFormData = () => {
    const defaultFormData = {
      activityType: '',
      hasEndDate: false,
    }
    dispatch(resetActivityData(defaultFormData))
  }

  const countryCode = useAppSelector(state => state.countryCode).countryCode
  const typeOfActivity = ['Welcome Activity', 'Login']

  const getActivityName = () => {
    const { activityType, uuid, activityName } = activityFormData || {}
    
    // For Points on Order Delivery, always use the activity name from form data
    if (activityType === ACTIVITY_TYPE.POINTS_ON_ORDER_DELIVERY) {
      return activityName
    }

    // Generate timestamp-based name for:
    // 1. New activities without UUID
    // 2. Welcome/Login activities without a custom name
    const shouldUseTimestampName = 
      !uuid || (typeOfActivity.includes(activityType) && !activityName)
    
    if (shouldUseTimestampName) {
      return `${getActivityEnum(activityType)}_${formatUTCTimestamp()}`
    }

    // Use custom activity name if available
    return activityName
  }


  const validateName = async name => {
    if (activityFormData?.activityType === ACTIVITY_TYPE.POINTS_ON_ORDER_DELIVERY) {
      if (!name || String(name).trim() === '') {
        dispatch(
          setToastNotifications({
            show: true,
            message: 'Activity Name is mandatory',
            type: 'error',
          })
        )
        return false
      }
      const id = activityFormData?.id ?? null
      const uuid = activityFormData?.uuid ?? null
      const params = {
        id,
        uuid,
        name
      }
      const resp = await handleNameValidationViaCallbackFn(validateActivityName, params)
      const isValid = resp?.success === true
      setIsNameValid(isValid)
      return isValid
    } else {
      setIsNameValid(true)
      return true
    }
  }

  // Shared helpers: segments normalization and payload builder
  const normalizeSegments = (input) => {
    if (!Array.isArray(input)) return []
    try {
      const isGrouped = input.some(Array.isArray)
      const flat = isGrouped ? input.flat() : input
      return Array.from(new Set(flat.filter(Boolean)))
    } catch {
      return []
    }
  }

  const segmentsPayloadFromForm = (formData) => {
    if (!Array.isArray(formData?.segments)) return {}
    const normalized = normalizeSegments(formData.segments)
    return normalized.length > 0 ? { segments: normalized } : {}
  }


  const isPointExpirationDuplicateError = (errorMessage) => {
    if (activityFormData?.activityType !== ACTIVITY_TYPE.POINT_EXPIRATION) {
      return false
    }
    const message = (errorMessage || '').toLowerCase()
    return message.includes('points expiry') || message.includes('already exists')
  }

  const fetchExistingPointExpirationActivity = async () => {
    const response = await handleGetActivity(`activityType=${ACTIVITY_TYPES_ENUM.POINT_EXPIRATION}`, false)
    return response?.data?.content?.[0]?.activityId || response?.data?.content?.[0]?.id
  }

  const handlePointExpirationDuplicateError = async () => {
    try {
      const existingActivityId = await fetchExistingPointExpirationActivity()
      handlePointExpirationPublishError(existingActivityId)
    } catch {
      handlePointExpirationPublishError(null)
    }
  }

  const handlePublish = async () => { 
    const uniqueActivityName = getActivityName()
    if(activityFormData?.status==null){
      const isValidName = await validateName(uniqueActivityName)
      if (!isValidName) return
    }
    
    const isEditingPublished = checkIsEditingPublished(activityFormData)
    
    // Add duration validation before publishing (skip for PRODUCT_PURCHASE activity type)
      let validationParams = {...activityFormData, activityName: uniqueActivityName}
      if(isEditingPublished){
        validationParams.id = activityFormData?.id || activityFormData?.activityId;
      } else {
        // delete validationParams.id;
      }
      const isDurationValid = await handleActivityDurationValidation(validationParams)
      if (!isDurationValid) return
    
    // these are the required fields for Login & Welcome API call
      const param = {
        applicationId: APPLICATION_ID[countryCode] || 1,
        title: '',
        activityType: getActivityEnum(activityFormData?.activityType), // WELCOME | LOGIN | POINTS_ON_ORDER_DELIVERY
        tags: '',
        slug: getSlug(activityFormData?.activityType), //common
        pointsEarned: activityFormData?.pointsEarned, // common
        maxLimitPerUser: 0,
        dailyLoginLimit: activityFormData?.dailyLoginLimit, // Login
        conversionRate: 0,
        amountPerUnit: 0,
        pointsAwardedPerUnit: 0,
        activityStartDate: getFormattedDateString(activityFormData?.startDate), // common
        activityEndDate: getFormattedDateString(activityFormData?.endDate), // common
        allTimeLoginLimit: activityFormData?.allTimeLoginLimit, // Login
        activityName: uniqueActivityName,
        ...(!isEditingPublished && { uuid: activityFormData?.uuid || getUUID(uniqueActivityName) }), // Use the determined final UUID
        orderType: OrderType[activityFormData?.orderType],
        localCurrency: activityFormData?.localCurrency,
        ...(COUNTRY_CODES_FREQUENCY.includes(countryCode)
          ? {numberOfCases: activityFormData?.numberOfCases}
          : {localCurrency: activityFormData?.localCurrency}),
        hasEndDate: activityFormData?.hasEndDate,
        receivePoints: activityFormData?.receivePoints,
        noOfDays: activityFormData?.noOfDays, // Point Expiration
        status: 'PUBLISHED',
        ...(isEditingPublished && { activityName: activityFormData.activityName }),
        ...(isEditingPublished && { id: activityFormData.id || activityFormData.activityId }), 
        allCustomers: activityFormData?.allCustomers,
        excludeSegments: activityFormData?.excludeSegments,
        ...segmentsPayloadFromForm(activityFormData),
      }

      const formBasedParams = Object.fromEntries(
        Object.entries(param).filter(([key, value]) => {
          const isFromFormData =
            key in activityFormData ||
            value === getActivityEnum(activityFormData?.activityType) ||
            value === getSlug(activityFormData?.activityType) ||
            value === getFormattedDateString(activityFormData?.startDate) ||
            value === getFormattedDateString(activityFormData?.endDate) ||
            value === OrderType[activityFormData?.orderType]

          const isManuallyIncluded = ['tags', 'applicationId', 'slug', 'activityName', 'uuid', 'status', 'id', 'segments', 'allCustomers', 'excludeSegments'].includes(key)
          if (isManuallyIncluded) {
            return true
          }

          return isFromFormData && value !== undefined && value !== null && value !== ''
        })
      )

      try {
        const response = isEditingPublished 
          ? await handleUpdateActivity({data: formBasedParams})
          : await handleCreateActivity({data: formBasedParams})
        
        if (response?.success) {
          dispatch(setActivityFormData(activityFormData))
          dispatch(setToastNotifications({show: true, message: isEditingPublished ? 'Activity updated successfully' : 'New Activity published', type: 'success'}))
          resetValidation()
          handleResetFormData()
          navigate(ROUTES.ACTIVITIES)
        } else {
          // Handle Point Expiration duplicate error from API response
          if (isPointExpirationDuplicateError(response?.message)) {
            await handlePointExpirationDuplicateError()
            return
          }
          
          const errMsg = response?.errorMessage || response?.message || 'An unknown error occurred'
          dispatch(setToastNotifications({show: true, message: errMsg, type: 'error'}))
        }
      } catch (error) {
        // Handle Point Expiration duplicate error from exception
        const errorMessage = error?.cause || error?.message || ''
        if (isPointExpirationDuplicateError(errorMessage)) {
          dispatch(setToastNotifications({show: false, message: '', type: 'error'}))
          await handlePointExpirationDuplicateError()
          return
        }
        
        const errMsg = error?.cause || error?.response?.data?.errorMessage || error?.response?.data?.message || error?.message || 'An unknown error occurred'
        dispatch(setToastNotifications({show: true, message: errMsg, type: 'error'}))
      }
  }

  const handleDraft = async () => {
    const uniqueActivityName = getActivityName()
    const isValidName = await validateName(uniqueActivityName)
    if (!isValidName) return
    else{
    let response

    // Build segments payload once and reuse to avoid repetition
    const segmentsPayload = () => segmentsPayloadFromForm(activityFormData)

    if (!activityFormData?.uuid) {
      response = await handleSaveDraftActivity({
        data: {
          ...activityFormData,
          activityName: uniqueActivityName,
          activityStartDate: getFormattedDateString(activityFormData?.startDate), // common
          activityEndDate: getFormattedDateString(activityFormData?.endDate), // common
          activityType: getActivityEnum(activityFormData?.activityType),
          status: 'DRAFT',
          uuid: getUUID(uniqueActivityName), // Use consistent UUID
          ...segmentsPayload(),
        },
      })
    } else {
      response = await handleUpdateDraftActivity({
        data: {
          ...activityFormData,
          activityName: uniqueActivityName,
          activityStartDate: getFormattedDateString(activityFormData?.startDate), // common
          activityEndDate: getFormattedDateString(activityFormData?.endDate), // common
          activityType: getActivityEnum(activityFormData?.activityType),
          status: 'DRAFT',
          uuid: activityFormData?.uuid, // Use consistent UUID
          ...segmentsPayload(),
        },
      })
    }

    if (response?.success) {
      dispatch(
        setToastNotifications({
          show: true,
          message: activityFormData?.uuid ? 'Activity draft updated' : 'New Activity draft',
          type: 'success',
        })
      )
      handleResetFormData()
      navigate(ROUTES.ACTIVITIES)
    } else {
      dispatch(setToastNotifications({show: true, message: 'Failed to save draft', type: 'error'}))
    }}
  }

  const handleCancel = () => {
    setIsCancel(!isCancel)
  }

  const onClickPrimaryButton = () => {
    handleResetFormData()
    navigate(ROUTES.ACTIVITIES)
  }

  const onClickSecondaryButton = () => {
    setIsCancel(!isCancel)
  }

  const handleValidationModalClose = () => {
    setIsNameValid(true)
  }

  const extractAndPreprocessData = formDataFromApi => {
    // Some list APIs return the payload under `data` key. Unwrap if present.
    const src = formDataFromApi?.data ?? formDataFromApi
    let {
      activityType,
      pointsEarned,
      hasEndDate,
      activityStartDate: startDate,
      activityEndDate: endDate,
      dailyLoginLimit,
      allTimeLoginLimit,
      activityName,
      orderType,
      localCurrency,
      numberOfCases,
      noOfDays,
      receivePoints,
      status,
      uuid,
      id,
      activityId,
      allCustomers,
      excludeSegments,
    } = src

    // Handle date field mapping
    startDate = src?.activeStartDate || src?.activityStartDate
    endDate = src?.activeEndDate || src?.activityEndDate

    // Convert numeric fields to strings
    if (pointsEarned) {
      pointsEarned = String(pointsEarned)
    }
    if (numberOfCases) {
      numberOfCases = String(numberOfCases)
    }
    if (receivePoints) {
      receivePoints = String(receivePoints)
    }
    if (noOfDays != null) {
      noOfDays = String(noOfDays)
    }

    // Set hasEndDate flag
    if (endDate && endDate !== null && endDate !== undefined) {
      hasEndDate = true;
    }

    // Normalize segments: support both 'segments' and 'Segments' from API
    const rawSegments = src?.segments ?? src?.Segments
    const normalizedSegments = Array.isArray(rawSegments)
      ? rawSegments
          .map(s => (typeof s === 'string' ? s : s?.id || s?.segmentName || s?.value))
          .filter(Boolean)
      : undefined

    return {
      activityType,
      pointsEarned,
      hasEndDate,
      startDate,
      endDate,
      dailyLoginLimit,
      allTimeLoginLimit,
      activityName,
      orderType,
      localCurrency,
      numberOfCases,
      noOfDays,
      receivePoints,
      status,
      uuid,
      id,
      activityId,
      segments: normalizedSegments,
      allCustomers,
      excludeSegments,
    }
  }

  const processDates = (startDate, endDate) => {
    const processedStartDate = getDateFromISOString(startDate)
    const processedEndDate = endDate ? getDateFromISOString(endDate) : endDate
    return { processedStartDate, processedEndDate }
  }

  const createWelcomeActivityOrder = (processedData) => {
    const {
      activityType,
      pointsEarned,
      hasEndDate,
      processedStartDate: startDate,
      activityName,
      uuid,
      status,
      activityId,
      processedEndDate: endDate,
      segments,
      allCustomers,
      excludeSegments,
    } = processedData

    return {
      activityType: activityType,
      pointsEarned: pointsEarned,
      hasEndDate: hasEndDate,
      startDate: startDate,
      ...(activityName && {activityName: activityName}),
      ...(uuid && {uuid: uuid}),
      ...(status && {status: status}),
      ...(activityId && {id: activityId}),
      ...(hasEndDate && {endDate: endDate}),
      ...(Array.isArray(segments) && segments.length > 0 && { segments }),
      ...(allCustomers && { allCustomers }),
      ...(excludeSegments && { excludeSegments }),
    }
  }

  const createLoginActivityOrder = (processedData) => {
    const {
      activityType,
      pointsEarned,
      dailyLoginLimit,
      allTimeLoginLimit,
      processedStartDate: startDate,
      processedEndDate: endDate,
      receivePoints,
      activityName,
      uuid,
      status,
      activityId,
      segments,
      allCustomers,
      excludeSegments,
    } = processedData

    return {
      activityType: activityType,
      pointsEarned: pointsEarned,
      ...(dailyLoginLimit && {dailyLoginLimit: dailyLoginLimit}),
      ...(allTimeLoginLimit && {allTimeLoginLimit: allTimeLoginLimit}),
      startDate: startDate,
      endDate: endDate,
      receivePoints: receivePoints,
      ...(activityName && {activityName: activityName}),
      ...(uuid && {uuid: uuid}),
      ...(status && {status: status}),
      ...(activityId && {id: activityId}),
      ...(Array.isArray(segments) && segments.length > 0 && { segments }),
      ...(allCustomers && { allCustomers }),
      ...(excludeSegments && { excludeSegments }),
    }
  }

  const createPointsOnOrderDeliveryActivityOrder = (processedData) => {
    const {
      activityType,
      activityName,
      orderType,
      uuid,
      status,
      activityId,
      processedStartDate: startDate,
      processedEndDate: endDate,
      numberOfCases,
      pointsEarned,
      localCurrency,
      segments,
      allCustomers,
      excludeSegments,
    } = processedData

    const isCountryCodeFrequency = COUNTRY_CODES_FREQUENCY.includes(countryCode)

    const fieldsArranged = {
      activityType: activityType,
      activityName: activityName,
      orderType: orderType,
      uuid: uuid,
      status: status,
      id: activityId,
      startDate: startDate,
      endDate: endDate,
      ...(Array.isArray(segments) && segments.length > 0 && { segments }),
      ...(allCustomers && { allCustomers }),
      ...(excludeSegments && { excludeSegments }),
    }

    // Add frequency-based fields
    if (isCountryCodeFrequency) {
      fieldsArranged.numberOfCases = numberOfCases
      fieldsArranged.pointsEarned = pointsEarned
    } else {
      fieldsArranged.localCurrency = localCurrency
      fieldsArranged.pointsEarned = pointsEarned
    }

    // Filter out any undefined or null values
    return Object.entries(fieldsArranged)
      .reduce((acc, [key, value]) => {
        if (value !== undefined && value !== null) {
          acc[key] = value;
        }
        return acc;
      }, {});
  }

  // Create Point Expiration activity order
  const createPointExpirationActivityOrder = (processedData) => {
    const {
      activityType,
      noOfDays,
      uuid,
      status,
      id,
      activityId,
      segments,
      allCustomers,
      excludeSegments,
    } = processedData

    // Use id if available, otherwise use activityId
    const numericId = id || activityId

    return {
      activityType: activityType,
      noOfDays: noOfDays,
      ...(uuid && {uuid: uuid}),
      ...(status && {status: status}),
      ...(numericId && {id: numericId}),
      ...(Array.isArray(segments) && segments.length > 0 && { segments }),
      ...(allCustomers && { allCustomers }),
      ...(excludeSegments && { excludeSegments }),
    }
  }

  const getFormDataWithOrder = formDataFromApi => {
    // Extract and preprocess data
    const extractedData = extractAndPreprocessData(formDataFromApi)
    
    // Process dates
    const { processedStartDate, processedEndDate } = processDates(extractedData.startDate, extractedData.endDate)
    
    // Get display activity type
    const displayActivityType = getDisplayActivityType(extractedData.activityType)
    
    // Combine all processed data
    const processedData = {
      ...extractedData,
      activityType: displayActivityType,
      processedStartDate,
      processedEndDate,
    }

    // Return appropriate order based on activity type
    if (displayActivityType === ACTIVITY_TYPE.WELCOME_ACTIVITY) {
      return createWelcomeActivityOrder(processedData)
    } else if (displayActivityType === ACTIVITY_TYPE.LOGIN) {
      return createLoginActivityOrder(processedData)
    } else if (displayActivityType === ACTIVITY_TYPE.POINTS_ON_ORDER_DELIVERY) {
      return createPointsOnOrderDeliveryActivityOrder(processedData)
    } else if (displayActivityType === ACTIVITY_TYPE.POINT_EXPIRATION) {
      return createPointExpirationActivityOrder(processedData)
    } else {
      return {}
    }
  }

  return {
    handlePublish,
    handleDraft,
    handleCancel,
    onClickPrimaryButton,
    onClickSecondaryButton,
    isCancel,
    activityFormData,
    getFormDataWithOrder,
    isNameValid,
    handleValidationModalClose,
    validateName,
    getActivityName,
    showToast,
    toastMessage,
    handleToastClose,
    handleViewActivity,
  }
}
